import type {
  KonvaAnnotationGeometry,
  KonvaAnnotationTemplate,
} from './annotationSchema'
import { getAbsoluteYFromSubPage } from './utils'

export type LineBasedAnnotation = KonvaAnnotationTemplate & {
  geometry: Extract<KonvaAnnotationGeometry, { mode: 'points' }>
  kind: 'line' | 'arrow' | 'bubble'
}

export type BoundsBasedShapeAnnotation = KonvaAnnotationTemplate & {
  geometry: Extract<KonvaAnnotationGeometry, { mode: 'bounds' }>
  kind:
    | 'rect'
    | 'roundRect'
    | 'circle'
    | 'ellipse'
    | 'triangle'
    | 'diamond'
    | 'icon'
}

// Coordinates inside an annotation's own Konva group.
export type LocalAnnotationRect = {
  height: number
  width: number
  x: number
  y: number
}

// Coordinates in the full document page/subpage space.
export type DocumentAnnotationBounds = {
  height: number
  left: number
  top: number
  width: number
}

export type SelectionFrame =
  | {
      mode: 'axis'
      center: { x: number; y: number }
      cornerRadius: number
      rect: LocalAnnotationRect
    }
  | {
      mode: 'oriented'
      angle: number
      center: { x: number; y: number }
      cornerRadius: number
      origin: { x: number; y: number }
      rect: LocalAnnotationRect
    }

export type ArrowTextLayout = {
  arrowPoints: number[]
  fontSize: number
  placement: 'middle' | 'bottom'
  text: string
  textCornerRadius: number
  textHeight: number
  textWidth: number
  textX: number
  textY: number
}

export type BubbleLayout = {
  bubbleCenter: { x: number; y: number }
  bubbleRadius: number
  linePoints: number[]
}

export type LineResizeHandleGeometry = {
  handleOffsetX: number
  handleOffsetY: number
  index: number
  x: number
  y: number
}

export type ShapeResizeHandleGeometry = {
  cursor: string
  horizontal: 'left' | 'center' | 'right'
  vertical: 'top' | 'middle' | 'bottom'
  x: number
  y: number
}

export type ShapeResizeLocalBounds = {
  bottom: number
  height: number
  left: number
  right: number
  top: number
  width: number
}

export const AXIS_SELECTION_PADDING = 6
export const DEFAULT_BUBBLE_RADIUS = 10
export const MIN_BOUNDS_ANNOTATION_HIT_TARGET_SIZE = 18
export const ORIENTED_POINT_SELECTION_END_PADDING = 5
export const LINE_RESIZE_HANDLE_OFFSET =
  ORIENTED_POINT_SELECTION_END_PADDING + 1

export const isLineBasedAnnotation = (
  annotation: KonvaAnnotationTemplate,
): annotation is LineBasedAnnotation =>
  annotation.geometry.mode === 'points' &&
  (annotation.kind === 'line' ||
    annotation.kind === 'arrow' ||
    annotation.kind === 'bubble')

export const isBoundsBasedShapeAnnotation = (
  annotation: KonvaAnnotationTemplate,
): annotation is BoundsBasedShapeAnnotation =>
  annotation.geometry.mode === 'bounds' &&
  (annotation.kind === 'rect' ||
    annotation.kind === 'roundRect' ||
    annotation.kind === 'circle' ||
    annotation.kind === 'ellipse' ||
    annotation.kind === 'triangle' ||
    annotation.kind === 'diamond' ||
    annotation.kind === 'icon')

export const getLineVector = (points: number[]) => {
  const [x1 = 0, y1 = 0, x2 = 0, y2 = 0] = points
  const dx = x2 - x1
  const dy = y2 - y1
  const length = Math.hypot(dx, dy)
  const ux = length ? dx / length : 0
  const uy = length ? dy / length : 0

  return {
    angleDeg: Math.atan2(dy, dx) * (180 / Math.PI),
    dx,
    dy,
    length,
    ux,
    uy,
    x1,
    x2,
    y1,
    y2,
  }
}

const rotatePoint = ({
  angle,
  point,
}: {
  angle: number
  point: { x: number; y: number }
}) => {
  if (!angle) return point

  const radians = (angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  return {
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos,
  }
}

const getDocumentBoundsFromPoints = (
  points: Array<{ x: number; y: number }>,
): DocumentAnnotationBounds => {
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  return {
    height: Math.max(maxY - minY, 1),
    left: minX,
    top: minY,
    width: Math.max(maxX - minX, 1),
  }
}

export const getPointExtents = ({
  maxXFallback = 0,
  maxYFallback = 0,
  minXFallback = 0,
  minYFallback = 0,
  points,
}: {
  maxXFallback?: number
  maxYFallback?: number
  minXFallback?: number
  minYFallback?: number
  points: number[]
}) => {
  const xs = points.filter((_, index) => index % 2 === 0)
  const ys = points.filter((_, index) => index % 2 === 1)
  const minX = Math.min(minXFallback, ...xs)
  const maxX = Math.max(maxXFallback, ...xs)
  const minY = Math.min(minYFallback, ...ys)
  const maxY = Math.max(maxYFallback, ...ys)

  return {
    height: Math.max(maxY - minY, 1),
    maxX,
    maxY,
    minX,
    minY,
    width: Math.max(maxX - minX, 1),
  }
}

export const getLocalAnnotationVisualRect = ({
  annotation,
  baseSize,
}: {
  annotation: KonvaAnnotationTemplate
  baseSize: { width: number; height: number }
}): LocalAnnotationRect => {
  if (annotation.geometry.mode === 'points') {
    const extents = getPointExtents({
      maxXFallback: annotation.geometry.width ?? 1,
      maxYFallback: annotation.geometry.height ?? 1,
      points: annotation.geometry.points,
    })

    return {
      height: extents.height,
      width: extents.width,
      x: extents.minX,
      y: extents.minY,
    }
  }

  return {
    height: Math.max(baseSize.height, 1),
    width: Math.max(baseSize.width, 1),
    x: 0,
    y: 0,
  }
}

const getRotatedDocumentBounds = ({
  angle,
  height,
  left,
  top,
  width,
}: DocumentAnnotationBounds & { angle: number }): DocumentAnnotationBounds => {
  if (!angle) {
    return { height, left, top, width }
  }

  const radians = (angle * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const corners = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ].map((point) => ({
    x: left + point.x * cos - point.y * sin,
    y: top + point.x * sin + point.y * cos,
  }))
  const xs = corners.map((point) => point.x)
  const ys = corners.map((point) => point.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  return {
    height: Math.max(maxY - minY, 1),
    left: minX,
    top: minY,
    width: Math.max(maxX - minX, 1),
  }
}

export const getDocumentAnnotationBounds = ({
  annotation,
  subPageIds,
}: {
  annotation: KonvaAnnotationTemplate
  subPageIds: string[]
}): DocumentAnnotationBounds => {
  const y = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })

  if (annotation.geometry.mode === 'bounds') {
    return getRotatedDocumentBounds({
      angle: annotation.geometry.rotation ?? 0,
      height: Math.max(annotation.geometry.height ?? 1, 1),
      left: annotation.geometry.x,
      top: y,
      width: Math.max(annotation.geometry.width ?? 1, 1),
    })
  }

  const extents = getPointExtents({
    points: annotation.geometry.points,
  })
  const padding = Math.max((annotation.style.strokeWidth ?? 2) * 2, 12)
  const rotation = annotation.geometry.rotation ?? 0
  const localPoints = [
    { x: extents.minX - padding, y: extents.minY - padding },
    { x: extents.maxX + padding, y: extents.minY - padding },
    { x: extents.maxX + padding, y: extents.maxY + padding },
    { x: extents.minX - padding, y: extents.maxY + padding },
  ]
  const textLayout = getArrowTextLayout(annotation)

  if (textLayout) {
    const textTop = textLayout.textY - 4

    localPoints.push(
      { x: textLayout.textX, y: textTop },
      { x: textLayout.textX + textLayout.textWidth, y: textTop },
      {
        x: textLayout.textX + textLayout.textWidth,
        y: textTop + textLayout.textHeight,
      },
      { x: textLayout.textX, y: textTop + textLayout.textHeight },
    )
  }

  return getDocumentBoundsFromPoints(
    localPoints.map((point) => {
      const rotatedPoint = rotatePoint({ angle: rotation, point })

      return {
        x: annotation.geometry.x + rotatedPoint.x,
        y: y + rotatedPoint.y,
      }
    }),
  )
}

export const getMinimumHitTargetDocumentBounds = ({
  bounds,
  minSize = MIN_BOUNDS_ANNOTATION_HIT_TARGET_SIZE,
}: {
  bounds: DocumentAnnotationBounds
  minSize?: number
}): DocumentAnnotationBounds => {
  const width = Math.max(bounds.width, minSize)
  const height = Math.max(bounds.height, minSize)

  return {
    height,
    left: bounds.left - (width - bounds.width) / 2,
    top: bounds.top - (height - bounds.height) / 2,
    width,
  }
}

export const getSelectionFrame = ({
  annotation,
  bubbleRadius = DEFAULT_BUBBLE_RADIUS,
  strokeWidth,
  visualRect,
}: {
  annotation: KonvaAnnotationTemplate
  bubbleRadius?: number
  strokeWidth: number
  visualRect: LocalAnnotationRect
}): SelectionFrame => {
  const axisPadding = strokeWidth / 2 + 1
  const axisFrame: SelectionFrame = {
    mode: 'axis',
    center: {
      x: visualRect.x + visualRect.width / 2,
      y: visualRect.y + visualRect.height / 2,
    },
    cornerRadius: 0,
    rect: {
      height: visualRect.height + axisPadding * 2,
      width: visualRect.width + axisPadding * 2,
      x: visualRect.x - axisPadding,
      y: visualRect.y - axisPadding,
    },
  }

  if (!isLineBasedAnnotation(annotation)) {
    return axisFrame
  }

  const { angleDeg, length, ux, uy, x1, y1 } = getLineVector(
    annotation.geometry.points,
  )

  if (!length) {
    return axisFrame
  }

  const resolvedBubbleRadius = annotation.kind === 'bubble' ? bubbleRadius : 0
  const halfHeight = Math.max(strokeWidth / 2, resolvedBubbleRadius, 6)
  const startPadding =
    annotation.kind === 'bubble'
      ? resolvedBubbleRadius + ORIENTED_POINT_SELECTION_END_PADDING
      : ORIENTED_POINT_SELECTION_END_PADDING
  const endPadding = ORIENTED_POINT_SELECTION_END_PADDING
  const centerDistance = (length + endPadding - startPadding) / 2

  return {
    mode: 'oriented',
    angle: angleDeg,
    center: {
      x: x1 + ux * centerDistance,
      y: y1 + uy * centerDistance,
    },
    cornerRadius: 0,
    origin: { x: x1, y: y1 },
    rect: {
      height: halfHeight * 2,
      width: length + startPadding + endPadding,
      x: -startPadding,
      y: -halfHeight,
    },
  }
}

export const getArrowTextLayout = (
  annotation: KonvaAnnotationTemplate,
): ArrowTextLayout | null => {
  if (
    annotation.kind !== 'arrow' ||
    annotation.geometry.mode !== 'points' ||
    !annotation.text?.enabled ||
    (annotation.text.placement !== 'middle' &&
      annotation.text.placement !== 'bottom')
  ) {
    return null
  }

  const { dx, dy, length, x1, y1 } = getLineVector(annotation.geometry.points)

  if (length < 24) {
    return null
  }

  const placement = annotation.text.placement
  const textWidth = annotation.text.boxWidth ?? 100
  const fontSize = Math.max(
    Math.floor((annotation.text.fontSize ?? 14) - 1.5),
    10,
  )
  const text = annotation.text.maxLength
    ? (annotation.text.value ?? '').slice(0, annotation.text.maxLength)
    : (annotation.text.value ?? '')
  const textX =
    placement === 'middle'
      ? x1 + dx / 2 - textWidth / 2
      : x1 - textWidth / 2 - 10
  const textY =
    placement === 'middle' ? y1 + dy / 2 - fontSize / 2 : y1 - fontSize / 2

  return {
    arrowPoints: [x1, y1, x1 + dx, y1 + dy],
    fontSize,
    placement,
    text,
    textCornerRadius: annotation.text.boxCornerRadius ?? 0,
    textHeight: annotation.text.boxHeight ?? 20,
    textWidth,
    textX: Math.round(textX),
    textY: Math.round(textY),
  }
}

export const getBubbleLayout = (
  annotation: KonvaAnnotationTemplate,
): BubbleLayout | null => {
  if (annotation.kind !== 'bubble' || annotation.geometry.mode !== 'points') {
    return null
  }

  const { length, ux, uy, x1, x2, y1, y2 } = getLineVector(
    annotation.geometry.points,
  )

  if (length === 0) {
    return {
      bubbleCenter: { x: x1, y: y1 },
      bubbleRadius: DEFAULT_BUBBLE_RADIUS,
      linePoints: [x1, y1, x1, y1],
    }
  }

  return {
    bubbleCenter: { x: x1, y: y1 },
    bubbleRadius: DEFAULT_BUBBLE_RADIUS,
    linePoints: [
      x1 + ux * DEFAULT_BUBBLE_RADIUS,
      y1 + uy * DEFAULT_BUBBLE_RADIUS,
      x2,
      y2,
    ],
  }
}

export const getLineBasedAnnotationDocumentVisualBounds = ({
  annotation,
  subPageIds,
}: {
  annotation: LineBasedAnnotation
  subPageIds: string[]
}): DocumentAnnotationBounds => {
  const absoluteY = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? subPageIds[0],
    relativeY: annotation.geometry.y,
    subPageIds,
  })
  const rotation = annotation.geometry.rotation ?? 0
  const linePoints =
    annotation.kind === 'bubble'
      ? (getBubbleLayout(annotation)?.linePoints ?? annotation.geometry.points)
      : (getArrowTextLayout(annotation)?.arrowPoints ??
        annotation.geometry.points)
  const { x1, y1, x2, y2 } = getLineVector(linePoints)
  const localPoints = [
    { x: x1, y: y1 },
    { x: x2, y: y2 },
  ]

  if (annotation.kind === 'bubble') {
    const bubbleLayout = getBubbleLayout(annotation)

    if (bubbleLayout) {
      const { bubbleCenter, bubbleRadius } = bubbleLayout

      localPoints.push(
        { x: bubbleCenter.x - bubbleRadius, y: bubbleCenter.y },
        { x: bubbleCenter.x + bubbleRadius, y: bubbleCenter.y },
        { x: bubbleCenter.x, y: bubbleCenter.y - bubbleRadius },
        { x: bubbleCenter.x, y: bubbleCenter.y + bubbleRadius },
      )
    }
  }

  return getDocumentBoundsFromPoints(
    localPoints.map((point) => {
      const rotatedPoint = rotatePoint({ angle: rotation, point })

      return {
        x: annotation.geometry.x + rotatedPoint.x,
        y: absoluteY + rotatedPoint.y,
      }
    }),
  )
}

export const getLineResizeHandleGeometry = (
  annotation: LineBasedAnnotation,
): LineResizeHandleGeometry[] => {
  const { length, ux, uy, x1, x2, y1, y2 } = getLineVector(
    annotation.geometry.points,
  )
  const startOffsetDistance =
    annotation.kind === 'bubble'
      ? DEFAULT_BUBBLE_RADIUS + LINE_RESIZE_HANDLE_OFFSET
      : LINE_RESIZE_HANDLE_OFFSET
  const startOffsetX = length ? ux * startOffsetDistance : 0
  const startOffsetY = length ? uy * startOffsetDistance : 0
  const endOffsetX = length ? ux * LINE_RESIZE_HANDLE_OFFSET : 0
  const endOffsetY = length ? uy * LINE_RESIZE_HANDLE_OFFSET : 0

  return [
    {
      handleOffsetX: -startOffsetX,
      handleOffsetY: -startOffsetY,
      index: 0,
      x: x1 - startOffsetX,
      y: y1 - startOffsetY,
    },
    {
      handleOffsetX: endOffsetX,
      handleOffsetY: endOffsetY,
      index: 1,
      x: x2 + endOffsetX,
      y: y2 + endOffsetY,
    },
  ]
}

export const getShapeResizeHandleGeometry = (
  annotation: BoundsBasedShapeAnnotation,
  { cornerOnly = false }: { cornerOnly?: boolean } = {},
): ShapeResizeHandleGeometry[] => {
  const width = Math.max(annotation.geometry.width, 1)
  const height = Math.max(annotation.geometry.height, 1)
  const strokeWidth = annotation.style.strokeWidth ?? 2
  const selectionFrame = getSelectionFrame({
    annotation,
    strokeWidth,
    visualRect: getLocalAnnotationVisualRect({
      annotation,
      baseSize: { height, width },
    }),
  })
  const left = selectionFrame.rect.x
  const right = selectionFrame.rect.x + selectionFrame.rect.width
  const top = selectionFrame.rect.y
  const bottom = selectionFrame.rect.y + selectionFrame.rect.height
  const midX = selectionFrame.rect.x + selectionFrame.rect.width / 2
  const midY = selectionFrame.rect.y + selectionFrame.rect.height / 2

  const handles: ShapeResizeHandleGeometry[] = [
    {
      cursor: 'nwse-resize',
      horizontal: 'left',
      vertical: 'top',
      x: left,
      y: top,
    },
    {
      cursor: 'ns-resize',
      horizontal: 'center',
      vertical: 'top',
      x: midX,
      y: top,
    },
    {
      cursor: 'nesw-resize',
      horizontal: 'right',
      vertical: 'top',
      x: right,
      y: top,
    },
    {
      cursor: 'ew-resize',
      horizontal: 'right',
      vertical: 'middle',
      x: right,
      y: midY,
    },
    {
      cursor: 'nwse-resize',
      horizontal: 'right',
      vertical: 'bottom',
      x: right,
      y: bottom,
    },
    {
      cursor: 'ns-resize',
      horizontal: 'center',
      vertical: 'bottom',
      x: midX,
      y: bottom,
    },
    {
      cursor: 'nesw-resize',
      horizontal: 'left',
      vertical: 'bottom',
      x: left,
      y: bottom,
    },
    {
      cursor: 'ew-resize',
      horizontal: 'left',
      vertical: 'middle',
      x: left,
      y: midY,
    },
  ]

  if (cornerOnly) {
    return handles.filter(
      (handle) =>
        handle.horizontal !== 'center' && handle.vertical !== 'middle',
    )
  }

  return handles
}

export const getShapeResizeLocalBounds = ({
  handle,
  height,
  lockAspectRatio = false,
  minSize,
  resizePoint,
  width,
}: {
  handle: ShapeResizeHandleGeometry
  height: number
  lockAspectRatio?: boolean
  minSize: number
  resizePoint: { x: number; y: number }
  width: number
}): ShapeResizeLocalBounds => {
  let left = 0
  let top = 0
  let right = width
  let bottom = height

  if (handle.horizontal === 'left') {
    left = Math.min(resizePoint.x, right - minSize)
  } else if (handle.horizontal === 'right') {
    right = Math.max(resizePoint.x, left + minSize)
  }

  if (handle.vertical === 'top') {
    top = Math.min(resizePoint.y, bottom - minSize)
  } else if (handle.vertical === 'bottom') {
    bottom = Math.max(resizePoint.y, top + minSize)
  }

  const isHorizontalResize = handle.horizontal !== 'center'
  const isVerticalResize = handle.vertical !== 'middle'
  const aspectRatio = width / height

  if (
    lockAspectRatio &&
    Number.isFinite(aspectRatio) &&
    aspectRatio > 0 &&
    isHorizontalResize
  ) {
    const resizedWidth = right - left
    const resizedHeight = bottom - top
    const minAspectWidth = Math.max(minSize, minSize * aspectRatio)
    const minAspectHeight = Math.max(minSize, minSize / aspectRatio)
    const widthFromHeight =
      Math.max(resizedHeight, minAspectHeight) * aspectRatio
    const useWidthAsDriver =
      !isVerticalResize ||
      Math.abs(resizedWidth - width) >= Math.abs(widthFromHeight - width)

    const nextWidth = useWidthAsDriver
      ? Math.max(resizedWidth, minAspectWidth)
      : widthFromHeight
    const nextHeight = useWidthAsDriver
      ? nextWidth / aspectRatio
      : Math.max(resizedHeight, minAspectHeight)

    if (handle.horizontal === 'left') {
      left = right - nextWidth
    } else {
      right = left + nextWidth
    }

    if (isVerticalResize) {
      if (handle.vertical === 'top') {
        top = bottom - nextHeight
      } else {
        bottom = top + nextHeight
      }
    } else {
      top = (height - nextHeight) / 2
      bottom = top + nextHeight
    }
  } else if (
    lockAspectRatio &&
    Number.isFinite(aspectRatio) &&
    aspectRatio > 0 &&
    isVerticalResize
  ) {
    const resizedHeight = bottom - top
    const minAspectHeight = Math.max(minSize, minSize / aspectRatio)
    const nextHeight = Math.max(resizedHeight, minAspectHeight)
    const nextWidth = nextHeight * aspectRatio

    if (handle.vertical === 'top') {
      top = bottom - nextHeight
    } else {
      bottom = top + nextHeight
    }

    left = (width - nextWidth) / 2
    right = left + nextWidth
  }

  return {
    bottom,
    height: Math.max(bottom - top, minSize),
    left,
    right,
    top,
    width: Math.max(right - left, minSize),
  }
}
